import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import * as authUtils from '@/lib/auth';

// User information type definition
interface User {
  username: string;
  role: string;
}

// Login request type definition
interface LoginRequest {
  username: string;
  password: string;
}

// Authentication context type definition
interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  login: (data: LoginRequest) => Promise<void>;
  logout: () => void;
  handleAuthError: () => void; // Add authentication error handling method
  error: string | null;
}

// Default values
const defaultAuthContext: AuthContextType = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  login: async () => {},
  logout: () => {},
  handleAuthError: () => {}, // Add default implementation
  error: null,
};

// Create authentication context
const AuthContext = createContext<AuthContextType>(defaultAuthContext);

// Authentication provider type definition
interface AuthProviderProps {
  children: ReactNode;
}

// Authentication provider component
export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { t } = useTranslation('auth');
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [user, setUser] = useState<User | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Initial authentication check
  useEffect(() => {
    // Skip initial authentication check on login page
    const isLoginPage = location.pathname === '/login';
    if (isLoginPage) {
      setIsLoading(false);
      return;
    }

    const initAuth = async () => {
      setIsLoading(true);
      try {
        // Get user information from local storage
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        }

        // Check authentication status
        const isAuth = authUtils.isAuthenticated();
        setIsAuthenticated(isAuth);

        if (!isAuth) {
          // Clear user information if not authenticated
          setUser(null);
          localStorage.removeItem('user');
          authUtils.logout();
        }
      } catch (err) {
        console.error('Authentication check failed:', err);
        setIsAuthenticated(false);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [location.pathname]);

  // Login process
  const login = async (data: LoginRequest) => {
    setIsLoading(true);
    setError(null);
    try {
      const success = await authUtils.login(data.username, data.password);

      if (success) {
        // Set user information
        const user: User = {
          username: data.username,
          role: 'user', // Should be modified to get from API response
        };

        localStorage.setItem('user', JSON.stringify(user));
        setIsAuthenticated(true);
        setUser(user);

        // Redirect to dashboard
        navigate('/');
      } else {
        throw new Error(t('invalidCredentials'));
      }
    } catch (err) {
      console.error('Login failed:', err);
      // Handle error type appropriately
      const errorMessage = err instanceof Error
        ? err.message
        : t('invalidCredentials');
      setError(errorMessage);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  // Logout process
  const logout = async () => {
    setIsLoading(true);
    try {
      await authUtils.logout();
      setIsAuthenticated(false);
      setUser(null);
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      setError(t('logoutFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  // Authentication error handling
  const handleAuthError = () => {
    // Reset authentication state
    setIsAuthenticated(false);
    setUser(null);

    // Remove token and user information from local storage
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Redirect to login page
    authUtils.logout().finally(() => {
      navigate('/login');
    })
  };

  // Context value
  const contextValue: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    handleAuthError, // Add authentication error handling method
    error,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

// Hook to use authentication context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
